
import { cn } from "@/lib/utils";
import { ContentCard } from "../content-card";
import { ContentItem } from "../content-card/types";
import { useContentSelection } from "@/contexts/selection/SelectionContext";
import { useEffect, MouseEvent } from "react";

interface ContentGridProps {
  contentItems: ContentItem[];
  onContentClick: (id: string) => void;
  onDeleteContent: (id: string) => void;
  onEditContent?: (id: string, currentTitle: string) => void;
  onArchiveContent?: (id: string) => void;
  onPin?: (id: string) => void;
  onUnpin?: (id: string) => void;
  collectionColor?: string | null;
  allContentIds: string[];
}

const ContentGrid = ({ 
  contentItems, 
  onContentClick, 
  onDeleteContent, 
  onEditContent,
  onArchiveContent,
  onPin,
  onUnpin,
  collectionColor,
  allContentIds
}: ContentGridProps) => {
  const { clearSelection } = useContentSelection();
  
  // Handle clicks outside content items to clear selection
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isContentCard = target.closest('[data-content-card]');
      const isSelectionCheckbox = target.closest('[data-selection-checkbox]');
      
      if (!isContentCard && !isSelectionCheckbox) {
        clearSelection();
      }
    };
    
    // Use capture phase to handle the event before it reaches card elements
    document.addEventListener('click', handleDocumentClick as any, { capture: true });
    
    return () => {
      document.removeEventListener('click', handleDocumentClick as any, { capture: true });
    };
  }, [clearSelection]);
  
  console.log('[COLOR DEBUG] ContentGrid rendering with:', {
    itemsCount: contentItems.length,
    collectionColor,
    firstItemColor: contentItems[0]?.collection_color
  });

  // Debug pinned items in grid
  const pinnedItems = contentItems.filter(item => item.pinned);
  console.log(`[PIN DEBUG] ContentGrid - Total items: ${contentItems.length}, Pinned items: ${pinnedItems.length}`);
  contentItems.forEach(item => {
    console.log(`[PIN DEBUG] ContentGrid - Item ${item.id} (${item.title}) pinned: ${item.pinned}`);
  });
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-6">
      {contentItems.length === 0 ? (
        <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500">No content items available</p>
        </div>
      ) : (
        contentItems.map((item) => {
          // Always use the item's own collection color if available
          console.log(`[COLOR DEBUG] ContentGrid - Item ${item.id} color: ${item.collection_color || 'none'}, using: ${item.collection_color || collectionColor || "#6B7789"}`);
          console.log(`[PIN DEBUG] ContentGrid - Rendering item ${item.id} with pinned: ${item.pinned}`);
          
          const handleCardClick = () => {
            console.log(`[NAVIGATION DEBUG] ContentGrid - Card clicked: ${item.id} (${item.title})`);
            onContentClick(item.id);
          };
          
          return (
            <div key={item.id} className="w-full">
              <ContentCard
                key={item.id}
                item={item}
                view="grid"
                onClick={handleCardClick}
                onDelete={onDeleteContent}
                onEdit={onEditContent}
                onArchive={onArchiveContent}
                onPin={onPin}
                onUnpin={onUnpin}
                allContentIds={allContentIds}
                selectable={true}
              />
            </div>
          );
        })
      )}
    </div>
  );
};

export default ContentGrid;
